var __typeError = (msg) => {
  throw TypeError(msg);
};
var __accessCheck = (obj, member, msg) => member.has(obj) || __typeError("Cannot " + msg);
var __privateGet = (obj, member, getter) => (__accessCheck(obj, member, "read from private field"), getter ? getter.call(obj) : member.get(obj));
var __privateAdd = (obj, member, value) => member.has(obj) ? __typeError("Cannot add the same private member more than once") : member instanceof WeakSet ? member.add(obj) : member.set(obj, value);
var __privateSet = (obj, member, value, setter) => (__accessCheck(obj, member, "write to private field"), setter ? setter.call(obj, value) : member.set(obj, value), value);
var __privateMethod = (obj, member, method) => (__accessCheck(obj, member, "access private method"), method);
var _storageKeys, _enokiClient, _encryption, _encryptionKey, _store, _EnokiFlow_instances, setSession_fn;
import { decodeSuiPrivateKey } from "@mysten/sui/cryptography";
import { Ed25519Keypair } from "@mysten/sui/keypairs/ed25519";
import { fromBase64, toBase64 } from "@mysten/sui/utils";
import { decodeJwt } from "jose";
import { atom, onMount, onSet } from "nanostores";
import { createDefaultEncryption } from "./encryption.js";
import { EnokiClient } from "./EnokiClient/index.js";
import { EnokiKeypair } from "./EnokiKeypair.js";
import { createSessionStorage } from "./stores.js";
const createStorageKeys = (apiKey) => ({
  STATE: `@enoki/flow/state/${apiKey}`,
  SESSION: `@enoki/flow/session/${apiKey}`
});
class EnokiFlow {
  constructor(config) {
    __privateAdd(this, _EnokiFlow_instances);
    __privateAdd(this, _storageKeys);
    __privateAdd(this, _enokiClient);
    __privateAdd(this, _encryption);
    __privateAdd(this, _encryptionKey);
    __privateAdd(this, _store);
    __privateSet(this, _enokiClient, new EnokiClient({
      apiKey: config.apiKey,
      apiUrl: config.apiUrl
    }));
    __privateSet(this, _encryptionKey, config.apiKey);
    __privateSet(this, _encryption, config.encryption ?? createDefaultEncryption());
    __privateSet(this, _store, config.store ?? createSessionStorage());
    __privateSet(this, _storageKeys, createStorageKeys(config.apiKey));
    let storedState = null;
    try {
      const rawStoredValue = __privateGet(this, _store).get(__privateGet(this, _storageKeys).STATE);
      if (rawStoredValue) {
        storedState = JSON.parse(rawStoredValue);
      }
    } catch {
    }
    this.$zkLoginState = atom(storedState || {});
    this.$zkLoginSession = atom({ initialized: false, value: null });
    onMount(this.$zkLoginSession, () => {
      this.getSession();
    });
    onSet(this.$zkLoginState, ({ newValue }) => {
      __privateGet(this, _store).set(__privateGet(this, _storageKeys).STATE, JSON.stringify(newValue));
    });
  }
  get enokiClient() {
    return __privateGet(this, _enokiClient);
  }
  async createAuthorizationURL(input) {
    const ephemeralKeyPair = new Ed25519Keypair();
    const { nonce, randomness, maxEpoch, estimatedExpiration } = await __privateGet(this, _enokiClient).createZkLoginNonce({
      network: input.network,
      ephemeralPublicKey: ephemeralKeyPair.getPublicKey()
    });
    const params = new URLSearchParams({
      ...input.extraParams,
      nonce,
      client_id: input.clientId,
      redirect_uri: input.redirectUrl,
      response_type: "id_token",
      // TODO: Eventually fetch the scopes for this client ID from the Enoki service:
      scope: [
        "openid",
        // Merge the requested scopes in with the required openid scopes:
        ...input.extraParams && "scope" in input.extraParams ? input.extraParams.scope : []
      ].filter(Boolean).join(" ")
    });
    let oauthUrl;
    switch (input.provider) {
      case "google": {
        oauthUrl = `https://accounts.google.com/o/oauth2/v2/auth?${params}`;
        break;
      }
      case "facebook": {
        oauthUrl = `https://www.facebook.com/v17.0/dialog/oauth?${params}`;
        break;
      }
      case "twitch": {
        params.set("force_verify", "true");
        oauthUrl = `https://id.twitch.tv/oauth2/authorize?${params}`;
        break;
      }
      default:
        throw new Error(`Invalid provider: ${input.provider}`);
    }
    this.$zkLoginState.set({ provider: input.provider });
    await __privateMethod(this, _EnokiFlow_instances, setSession_fn).call(this, {
      expiresAt: estimatedExpiration,
      maxEpoch,
      randomness,
      ephemeralKeyPair: toBase64(decodeSuiPrivateKey(ephemeralKeyPair.getSecretKey()).secretKey)
    });
    return oauthUrl;
  }
  // TODO: Should our SDK manage this automatically in addition to exposing a method?
  async handleAuthCallback(hash = window.location.hash) {
    const params = new URLSearchParams(hash.startsWith("#") ? hash.slice(1) : hash);
    const zkp = await this.getSession();
    if (!zkp || !zkp.ephemeralKeyPair || !zkp.maxEpoch || !zkp.randomness) {
      throw new Error(
        "Start of sign-in flow could not be found. Ensure you have started the sign-in flow before calling this."
      );
    }
    const jwt = params.get("id_token");
    if (!jwt) {
      throw new Error("Missing ID Token");
    }
    const decodedJwt = decodeJwt(jwt);
    if (!decodedJwt.sub || !decodedJwt.aud || typeof decodedJwt.aud !== "string") {
      throw new Error("Missing JWT data");
    }
    const { address, salt } = await __privateGet(this, _enokiClient).getZkLogin({ jwt });
    this.$zkLoginState.set({
      ...this.$zkLoginState.get(),
      salt,
      address
    });
    await __privateMethod(this, _EnokiFlow_instances, setSession_fn).call(this, {
      ...zkp,
      jwt
    });
    return params.get("state");
  }
  async getSession() {
    if (this.$zkLoginSession.get().initialized) {
      return this.$zkLoginSession.get().value;
    }
    try {
      const storedValue = __privateGet(this, _store).get(__privateGet(this, _storageKeys).SESSION);
      if (!storedValue) return null;
      const state = JSON.parse(
        await __privateGet(this, _encryption).decrypt(__privateGet(this, _encryptionKey), storedValue)
      );
      if (state?.expiresAt && Date.now() > state.expiresAt) {
        await this.logout();
      } else {
        this.$zkLoginSession.set({ initialized: true, value: state });
      }
    } catch {
      this.$zkLoginSession.set({ initialized: true, value: null });
    }
    return this.$zkLoginSession.get().value;
  }
  async logout() {
    this.$zkLoginState.set({});
    __privateGet(this, _store).delete(__privateGet(this, _storageKeys).STATE);
    await __privateMethod(this, _EnokiFlow_instances, setSession_fn).call(this, null);
  }
  // TODO: Should this return the proof if it already exists?
  async getProof({ network } = {}) {
    const zkp = await this.getSession();
    const { salt } = this.$zkLoginState.get();
    if (zkp?.proof) {
      if (zkp.expiresAt && Date.now() > zkp.expiresAt) {
        throw new Error("Stored proof is expired.");
      }
      return zkp.proof;
    }
    if (!salt || !zkp || !zkp.jwt) {
      throw new Error("Missing required parameters for proof generation");
    }
    const ephemeralKeyPair = Ed25519Keypair.fromSecretKey(fromBase64(zkp.ephemeralKeyPair));
    const proof = await __privateGet(this, _enokiClient).createZkLoginZkp({
      network,
      jwt: zkp.jwt,
      maxEpoch: zkp.maxEpoch,
      randomness: zkp.randomness,
      ephemeralPublicKey: ephemeralKeyPair.getPublicKey()
    });
    await __privateMethod(this, _EnokiFlow_instances, setSession_fn).call(this, {
      ...zkp,
      proof
    });
    return proof;
  }
  async getKeypair({ network } = {}) {
    await this.getProof({ network });
    const zkp = await this.getSession();
    const { address } = this.$zkLoginState.get();
    if (!address || !zkp || !zkp.proof) {
      throw new Error("Missing required data for keypair generation.");
    }
    if (Date.now() > zkp.expiresAt) {
      throw new Error("Stored proof is expired.");
    }
    return new EnokiKeypair({
      address,
      maxEpoch: zkp.maxEpoch,
      proof: zkp.proof,
      ephemeralKeypair: Ed25519Keypair.fromSecretKey(fromBase64(zkp.ephemeralKeyPair))
    });
  }
  async sponsorTransaction({
    network,
    transaction,
    client
  }) {
    const session = await this.getSession();
    if (!session || !session.jwt) {
      throw new Error("Missing required data for sponsorship.");
    }
    const transactionKindBytes = await transaction.build({
      onlyTransactionKind: true,
      client
    });
    return await __privateGet(this, _enokiClient).createSponsoredTransaction({
      jwt: session.jwt,
      network,
      transactionKindBytes: toBase64(transactionKindBytes)
    });
  }
  async executeTransaction({
    network,
    bytes,
    digest,
    client
  }) {
    const keypair = await this.getKeypair({ network });
    const userSignature = await keypair.signTransaction(fromBase64(bytes));
    await __privateGet(this, _enokiClient).executeSponsoredTransaction({
      digest,
      signature: userSignature.signature
    });
    await client.waitForTransaction({ digest });
    return { digest };
  }
  async sponsorAndExecuteTransaction({
    network,
    transaction,
    client
  }) {
    const { bytes, digest } = await this.sponsorTransaction({
      network,
      transaction,
      client
    });
    return await this.executeTransaction({ network, bytes, digest, client });
  }
}
_storageKeys = new WeakMap();
_enokiClient = new WeakMap();
_encryption = new WeakMap();
_encryptionKey = new WeakMap();
_store = new WeakMap();
_EnokiFlow_instances = new WeakSet();
setSession_fn = async function(newValue) {
  if (newValue) {
    const storedValue = await __privateGet(this, _encryption).encrypt(
      __privateGet(this, _encryptionKey),
      JSON.stringify(newValue)
    );
    __privateGet(this, _store).set(__privateGet(this, _storageKeys).SESSION, storedValue);
  } else {
    __privateGet(this, _store).delete(__privateGet(this, _storageKeys).SESSION);
  }
  this.$zkLoginSession.set({ initialized: true, value: newValue });
};
export {
  EnokiFlow
};
//# sourceMappingURL=EnokiFlow.js.map
